import React, { useState } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Badge,
  Select,
  TextInput,
  Grid,
  Tabs,
  Alert,
  ActionIcon,
  Menu,
  rem,
  Pagination,
  SimpleGrid,
  Divider,
  Image,
  Modal,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconCalendar,
  IconActivity,
  IconPhoto,
  IconClock,
  IconMapPin,
  IconEye,
  IconDownload,
  IconShare,
  IconPlayerPlay,
  IconSun,
  IconMoon,
  IconChevronLeft,
  IconDots,
  IconRefresh,
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { useParams, useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {

  useEnhancedChildActivities,

  useChildSummaryFromStudent
} from '../../hooks/useParent';
import { SmartLoader } from '../../components/common/LoadingStates';
import ActivityTimeline from '../../components/activities/ActivityTimeline';
import { renderLocation } from '../../utils/locationUtils';

const ChildActivities: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activityType, setActivityType] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'timeline' | 'grid'>('timeline');
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);

  // Use enhanced hooks for better data integration
  const {
    data: child,
    isLoading: childLoading,
    error: childError,
  } = useChildSummaryFromStudent(childId);

  const {
    data: enhancedData,
    isLoading: activitiesLoading,
    error: activitiesError,
    refetch: refetchActivities,
  } = useEnhancedChildActivities(childId, {
    start_date: dateRange[0]?.toISOString().split('T')[0],
    end_date: dateRange[1]?.toISOString().split('T')[0],
    activity_type: activityType || undefined,
    page: currentPage,
    limit: 20,
  });

  const activities = enhancedData?.activities || [];

  const activityTypes = [
    { value: '', label: 'All Activities' },
    { value: 'play', label: 'Play Time' },
    { value: 'meal', label: 'Meal Time' },
    { value: 'sleep', label: 'Rest Time' },
    { value: 'art', label: 'Art & Crafts' },
    { value: 'learning', label: 'Learning' },
    { value: 'outdoor', label: 'Outdoor Play' },
    { value: 'music', label: 'Music Time' },
    { value: 'story', label: 'Story Time' },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'play': return <IconPlayerPlay size={16} />;
      case 'meal': return <IconSun size={16} />;
      case 'sleep': return <IconMoon size={16} />;
      case 'art': return <IconActivity size={16} />;
      case 'learning': return <IconActivity size={16} />;
      case 'outdoor': return <IconPlayerPlay size={16} />;
      case 'music': return <IconActivity size={16} />;
      case 'story': return <IconActivity size={16} />;
      default: return <IconActivity size={16} />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'play': return 'green';
      case 'meal': return 'orange';
      case 'sleep': return 'violet';
      case 'art': return 'pink';
      case 'learning': return 'blue';
      case 'outdoor': return 'teal';
      case 'music': return 'grape';
      case 'story': return 'indigo';
      default: return 'gray';
    }
  };

  const handleActivityClick = (activity: any) => {
    setSelectedActivity(activity);
    openDetailModal();
  };

  const handleDownloadReport = () => {
    notifications.show({
      title: 'Download Started',
      message: 'Activity report is being prepared for download',
      color: 'blue',
    });
  };

  const handleRefresh = () => {
    refetchActivities();
    notifications.show({
      title: 'Refreshed',
      message: 'Activities have been updated',
      color: 'green',
    });
  };

  if (!childId) {
    return (
      <Alert color="red" title="Invalid Child ID">
        Please select a valid child to view their activities.
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header with Child Info */}
      <SmartLoader isLoading={childLoading} error={childError as Error | null}>
        {child && (
          <Card withBorder>
            <Group justify="space-between">
              <Group>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => navigate('/parent/dashboard')}
                >
                  <IconChevronLeft size={20} />
                </ActionIcon>
                <div>
                  <Title order={2}>{child.first_name}'s Activities</Title>
                  <Text size="sm" c="dimmed">
                    Track daily activities and engagement
                  </Text>
                </div>
              </Group>
              <Group>
                <ActionIcon variant="subtle" onClick={handleRefresh}>
                  <IconRefresh size={18} />
                </ActionIcon>
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <ActionIcon variant="subtle">
                      <IconDots size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                      onClick={handleDownloadReport}
                    >
                      Download Report
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPhoto style={{ width: rem(14), height: rem(14) }} />}
                      onClick={() => navigate(`/parent/child/${childId}/photos`)}
                    >
                      View Photos
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconShare style={{ width: rem(14), height: rem(14) }} />}
                    >
                      Share Activities
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          </Card>
        )}
      </SmartLoader>

      {/* Filters */}
      <Card withBorder>
        <Title order={4} mb="md">Filters</Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              placeholder="Search activities..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              placeholder="Activity type"
              leftSection={<IconFilter size={16} />}
              data={activityTypes}
              value={activityType}
              onChange={(value) => setActivityType(value || '')}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <DatePickerInput
              type="range"
              placeholder="Select date range"
              leftSection={<IconCalendar size={16} />}
              value={dateRange}
              onChange={setDateRange}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Group justify="flex-end" h="100%">
              <Tabs value={viewMode} onChange={(value) => setViewMode(value as any)}>
                <Tabs.List>
                  <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
                  <Tabs.Tab value="grid">Grid</Tabs.Tab>
                </Tabs.List>
              </Tabs>
            </Group>
          </Grid.Col>
        </Grid>
      </Card>

      {/* Activities Content */}
      <SmartLoader
        isLoading={activitiesLoading}
        error={activitiesError as Error | null}
        isEmpty={!activities?.length}
        emptyState={
          <Alert color="blue" title="No activities found">
            {searchQuery || activityType || dateRange[0]
              ? "No activities match your current filters. Try adjusting your search criteria."
              : "No activities have been recorded for this child yet."
            }
          </Alert>
        }
      >
        {child && activities && (
          <>
            {viewMode === 'timeline' ? (
              <Card withBorder>
                <Title order={4} mb="md">Activity Timeline</Title>
                <ActivityTimeline
                  studentId={childId!}
                  student={child}
                  maxItems={50}
                  showFilters={false}
                  showHeader={false}
                  dateRange={dateRange[0] && dateRange[1] ? {
                    start: dateRange[0],
                    end: dateRange[1],
                  } : undefined}
                />
              </Card>
            ) : (
              <div>
                <Card withBorder mb="md">
                  <Group justify="space-between">
                    <Title order={4}>Activities Grid View</Title>
                    <Text size="sm" c="dimmed">
                      {activities.length} activities found
                    </Text>
                  </Group>
                </Card>

                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                  {activities.map((activity: any) => (
                    <Card
                      key={activity.id}
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleActivityClick(activity)}
                    >
                      <Group justify="space-between" mb="sm">
                        <Badge
                          leftSection={getActivityIcon(activity.activity_type)}
                          color={getActivityColor(activity.activity_type)}
                          variant="light"
                        >
                          {activity.activity_type?.replace('_', ' ') || 'unknown'}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {new Date(activity.timestamp).toLocaleTimeString()}
                        </Text>
                      </Group>

                      <Text fw={500} mb="xs">
                        {activity.description || `${activity.activity_type?.replace('_', ' ') || 'Unknown'} Activity`}
                      </Text>

                      <Group gap="xs" mb="sm">
                        <Group gap={4}>
                          <IconMapPin size={12} />
                          <Text size="xs" c="dimmed">
                            {renderLocation(activity.location)}
                          </Text>
                        </Group>
                        <Group gap={4}>
                          <IconClock size={12} />
                          <Text size="xs" c="dimmed">
                            {new Date(activity.timestamp).toLocaleDateString()}
                          </Text>
                        </Group>
                      </Group>

                      {activity.teacher_notes && (
                        <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }} mb="sm">
                          "{activity.teacher_notes}"
                        </Text>
                      )}

                      {activity.photos && activity.photos.length > 0 && (
                        <Group gap="xs" mt="sm">
                          <IconPhoto size={14} />
                          <Text size="xs" c="dimmed">
                            {activity.photos.length} photo{activity.photos.length !== 1 ? 's' : ''}
                          </Text>
                        </Group>
                      )}

                      <Group justify="space-between" mt="sm">
                        <Badge size="xs" variant="filled" color="blue">
                          {Math.round(activity.confidence * 100)}% confidence
                        </Badge>
                        <ActionIcon variant="subtle" size="sm">
                          <IconEye size={14} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>

                {/* Pagination */}
                <Group justify="center" mt="xl">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={Math.ceil((activities?.length || 0) / 20)}
                  />
                </Group>
              </div>
            )}
          </>
        )}
      </SmartLoader>

      {/* Activity Detail Modal */}
      <Modal
        opened={detailModalOpened}
        onClose={closeDetailModal}
        title="Activity Details"
        size="lg"
      >
        {selectedActivity && (
          <Stack gap="md">
            <Group justify="space-between">
              <Badge
                leftSection={getActivityIcon(selectedActivity.activity_type)}
                color={getActivityColor(selectedActivity.activity_type)}
                size="lg"
              >
                {selectedActivity.activity_type?.replace('_', ' ') || 'unknown'}
              </Badge>
              <Text c="dimmed">
                {new Date(selectedActivity.timestamp).toLocaleString()}
              </Text>
            </Group>

            <Divider />

            <div>
              <Text fw={600} mb="xs">Description</Text>
              <Text>
                {selectedActivity.description || `${selectedActivity.activity_type?.replace('_', ' ') || 'Unknown'} activity was detected.`}
              </Text>
            </div>

            <Group>
              <div>
                <Text fw={600} mb="xs">Location</Text>
                <Group gap={4}>
                  <IconMapPin size={16} />
                  <Text>
                    {renderLocation(selectedActivity.location)}
                  </Text>
                </Group>
              </div>
              <div>
                <Text fw={600} mb="xs">Confidence</Text>
                <Badge color="blue">
                  {Math.round(selectedActivity.confidence * 100)}%
                </Badge>
              </div>
            </Group>

            {selectedActivity.teacher_notes && (
              <div>
                <Text fw={600} mb="xs">Teacher's Note</Text>
                <Text style={{ fontStyle: 'italic' }}>
                  "{selectedActivity.teacher_notes}"
                </Text>
              </div>
            )}

            {selectedActivity.photos && selectedActivity.photos.length > 0 && (
              <div>
                <Text fw={600} mb="xs">Photos ({selectedActivity.photos.length})</Text>
                <SimpleGrid cols={3} spacing="sm">
                  {selectedActivity.photos.map((_photo: string, index: number) => (
                    <Image
                      key={index}
                      src={null}
                      alt={`Activity photo ${index + 1}`}
                      fallbackSrc="https://via.placeholder.com/150x150?text=Photo"
                      radius="md"
                      h={100}
                    />
                  ))}
                </SimpleGrid>
              </div>
            )}

            <Group justify="flex-end" mt="md">
              <Button
                variant="light"
                leftSection={<IconShare size={16} />}
                onClick={() => {
                  notifications.show({
                    title: 'Shared',
                    message: 'Activity details have been shared',
                    color: 'green',
                  });
                  closeDetailModal();
                }}
              >
                Share
              </Button>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => {
                  notifications.show({
                    title: 'Download Started',
                    message: 'Activity details are being prepared for download',
                    color: 'blue',
                  });
                }}
              >
                Download
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default ChildActivities;
